import { DateTime, DateTimeFormatOptions, Interval } from "luxon";
import { useTranslation } from "react-i18next";
import { start } from "repl";


export const useFormatting = () => {
    const locale = useTranslation().i18n.language ?? "sv";
    const dateFormatter = useDateFormatter(locale);
    const numberFormatter = useNumberFormatted(locale);

    return {
        ...numberFormatter,
        // Date Epoch
        ...dateFormatter,
    }
}

// Num Formatting
const useNumberFormatted = (locale: string) => {

    return {
        intToSeparatedString: (e: number) => {
            if (e) {
                return e.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
            }
            return "-";
        },
        floatToPercent: (e: number) => {
            if (e) {
                const n = Math.round(e * 1000) / 10;
                return n + "%";
            }
            return "-";
        },
        numberDefault: (e: number) => {
            return new Intl.NumberFormat().format(e);
        }
    }
}

const isDateKeyValid = (datekey: number) => {
    if (!datekey) { return false; }

    if (datekey < 10000000) { return false; }
    if (datekey > 99999999) { return false; }

    return true;
}
// ----------------------------------------------------------------
// ----------------------------- Date -----------------------------
// ----------------------------------------------------------------
// Custom formats
const DATE_MED_WITHOUT_YEAR: DateTimeFormatOptions = { month: "long", day: "numeric" };

const useDateFormatter = (locale: string) => {
    const { t } = useTranslation();

    return {
        daysToEpoch: (epochSeconds: number): number => {
            // console.log(epoch);
            if (!epochSeconds) { return 0; }

            const e: number = typeof epochSeconds === 'string' ? parseInt(epochSeconds) : epochSeconds;

            const past = new Date(e * 1000);
            const now = new Date();
            const diff = past.valueOf() - now.valueOf();

            return Math.ceil(diff / (1000 * 60 * 60 * 24));
        },
        // 2025-03-14
        epochToDate: (epoch: number): string | null => {
            if (!epoch) { return ""; }
            const e: number = typeof epoch === 'string' ? parseInt(epoch) : epoch;
            const dt: DateTime = DateTime.fromSeconds(e);
            return dt.toLocal().setLocale(locale).toISODate();
        },
        // 2025-03-14 14:42:40
        epochToDateTimeUtc: (epoch: number): string => {
            if (!epoch) { return ""; }
            const e: number = typeof epoch === 'string' ? parseInt(epoch) : epoch;
            const dt: DateTime = DateTime.fromSeconds(e);
            return dt.toFormat("yyyy-LL-dd HH:mm:ss");
        },
        // 14 mars 2025 14:42:40
        epochToDateTimeLong: (epoch: number): string => {
            if (!epoch) { return ""; }

            const e: number = typeof epoch === 'string' ? parseInt(epoch) : epoch;
            const dt: DateTime = DateTime.fromSeconds(e);

            return dt.setLocale(locale).toLocal().toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS); // DATETIME_MED_WITH_SECONDS
        },
        // 
        epochToFriendlyDatetime: (epoch: number): string => {
            if (!epoch) { return ""; }

            const e: number = typeof epoch === 'string' ? parseInt(epoch) : epoch;
            const dt: DateTime = DateTime.fromSeconds(e);

            return dt.setLocale(locale).toLocal().toLocaleString(DateTime.DATETIME_MED); // DATETIME_MED_WITH_SECONDS
        },
        // 
        epochToTimeAgo: (epochSeconds: number) => {
            // console.log(epoch);
            if (!epochSeconds) { return ""; }

            const e: number = typeof epochSeconds === 'string' ? parseInt(epochSeconds) : epochSeconds;

            const past = new Date(e * 1000);
            const now = new Date();
            const diff = now.valueOf() - past.valueOf();

            const seconds = Math.floor(diff / (1000));
            const minutes = Math.floor(diff / (1000 * 60));
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const weeks = Math.floor(diff / (1000 * 60 * 60 * 24 * 7));
            const years = Math.floor(diff / (1000 * 60 * 60 * 24 * 365));

            // console.log("seconds:", seconds);
            // console.log("minutes:", minutes);
            // console.log("hours:", hours);
            // console.log("days:", days);
            // console.log("weeks:", weeks);
            // console.log("years:", years);

            if (years > 0) {
                return years == 1 ? t("aYearAgo") : t("XYearsAgo", { count: years });
            } else if (weeks > 0) {
                return weeks == 1 ? t("aWeekAgo") : t("XWeeksAgo", { count: weeks });
            } else if (days > 0) {
                return days == 1 ? t("aDayAgo") : t("XDaysAgo", { count: days });
            } else if (hours > 0) {
                return hours == 1 ? t("anHourAgo") : t("XhoursAgo", { count: hours });
            } else if (minutes > 0) {
                return minutes == 1 ? t("aMinuteAgo") : t("XMinutesAgo", { count: minutes });
            } else if (seconds > 0) {
                return seconds == 1 ? t("aSecondAgo") : t("XSecondsAgo", { count: seconds });
            } else {
                return "nu"
            }
        },
        // 20250430 => 30 april 2025
        datekeyToDate: (datekey: number, defaultValue?: string) => {
            const _default = defaultValue ?? "#err#";
            if (!datekey) { return _default; }

            if (datekey < 10000000) { return _default; }
            if (datekey > 99999999) { return _default; }

            const y = parseInt(datekey.toString()?.substring(0, 4));
            const m = parseInt(datekey.toString()?.substring(4, 6));
            const d = parseInt(datekey.toString()?.substring(6, 8));
            const dt: DateTime = DateTime.fromObject({ year: y, month: m, day: d })
            if (DateTime.now().year == y) {
                return dt.setLocale(locale).toLocaleString(DATE_MED_WITHOUT_YEAR);
            } else {
                return dt.setLocale(locale).toLocaleString(DateTime.DATE_FULL);
            }
        },
        // 202504 => april 2025
        monthkeyToDate: (datekey: number, defaultValue?: string) => {
            const _default = defaultValue ?? "#err#";
            if (!datekey) { return _default; }

            if (datekey < 100000) { return _default; }
            if (datekey > 999999) { return _default; }

            const y = parseInt(datekey.toString()?.substring(0, 4));
            const m = parseInt(datekey.toString()?.substring(4, 6));
            const dt: DateTime = DateTime.fromObject({ year: y, month: m, day: 1 })
            return dt.setLocale(locale).toFormat("LLLL yyyy");
        }
    }
}

export const useDateFunctions = () => {


    const dateKeyFormat = "yyyyMMdd";
    const dateKeyFormatShort = "yyyyMM";
    const isDateKey = (k: number) => {
        return k > 20000101 && k < 25000101;
    }
    const dateKeyToDate = (k: number): DateTime => {
        return DateTime.fromFormat(k.toString(), dateKeyFormat);
    }

    return {
        isDateKeyBetweenDateKeys: (startDateKey: number, endDateKey: number, dateKeyToTest: number) => {
            if (!isDateKeyValid(startDateKey) || !isDateKeyValid(endDateKey) || !isDateKeyValid(dateKeyToTest)) {
                return false;
            } else {
                return dateKeyToTest >= startDateKey && dateKeyToTest <= endDateKey;
            }
        },
        getDateKey: () => {
            return DateTime.now().year * 10000 + DateTime.now().month * 100 + DateTime.now().day;
        },
        addDaysToDateKey: (datekey: number, days: number) => {
            if (isDateKey(datekey)) {
                return parseInt(dateKeyToDate(datekey).plus({ days: days }).toFormat(dateKeyFormat));
            } else {
                return null;
            }
        },
        daysBetweenDateKeys: (datekeyFirst: number, datekeySecond: number): number => {
            if (isDateKey(datekeyFirst) && isDateKey(datekeySecond)) {
                const first = dateKeyToDate(datekeyFirst);
                const second = dateKeyToDate(datekeySecond);
                return second.diff(first, ["days"]).toObject()?.days ?? 0;
            } else {
                return -1;
            }
        }
    }
}